import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SESSION_STORAGE_KEYS } from '@wz-constants';
import {
  AddressModels,
  brokerRoutes,
  CommonModels,
  CommonRiskModels,
  HealthModels,
} from '@wz-models';
import { dateFromISOString } from '@wz-utilities/date.utilities';
import { differenceInMinutes, formatDistance } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  StepperServiceBase,
  StepperStepDefinition,
} from 'libs/modules/tunnelsante-ui/components/stepper2/stepper.service';
import { getQuotationFormFromRisk } from 'libs/modules/tunnelsante/projectors/health-quotation-projector';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { BaseNavigationService } from './base-navigation.service';
import { LoggerService } from './logger/logger.service';
import { SessionStorageService } from './storage/session-storage.service';
import Address = AddressModels.Address;
import CivilityEnum = CommonModels.CivilityEnum;
import Resiliation = HealthModels.Resilisation;
import InsuranceTypeEnum = CommonModels.InsuranceTypeEnum;

const STEPS_DEFINITION: StepperStepDefinition[] = [
  {
    label: 'Devis',
    route: brokerRoutes.APP_HEALTH_INSURANCE_QUOTATION,
  },
  {
    label: 'Tarif',
    route: brokerRoutes.APP_HEALTH_INSURANCE_PRICING,
  },
  {
    label: "Demande d'adhésion",
    route: brokerRoutes.APP_HEALTH_INSURANCE_SUBSCRIPTION,
  },
  {
    label: 'Signature',
    route: brokerRoutes.APP_HEALTH_INSURANCE_SIGNATURE,
    containerClass: 'wz-full-container',
  },
  {
    label: 'Confirmation',
    route: brokerRoutes.APP_HEALTH_INSURANCE_CONFIRMATION,
  },
];

@Injectable({ providedIn: 'root' })
export class HealthInsuranceService extends StepperServiceBase {
  steps = STEPS_DEFINITION;
  private currentData!: HealthModels.HealthQuotationStoredData | null;
  currentDataClientMTXSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );
  currentDataClientMTX: any;

  constructor(
    logger: LoggerService,
    route: ActivatedRoute,
    private brokerNavService: BaseNavigationService,
    private sessionStorage: SessionStorageService,
  ) {
    super(logger.getLogger('HealthInsuranceService'), route);
  }

  navigate(route: string[]) {
    this.brokerNavService.navigate(route);
  }

  getStoredData(): HealthModels.HealthQuotationStoredData {
    const risk =
      this.sessionStorage.getObject<HealthModels.HealthQuotationStoredData>(
        SESSION_STORAGE_KEYS.HealthQuotationData,
      );
    if (risk) {
      this.currentData = risk;
      risk.quotationNavigator = this.getInfoNavigator();
    }
    // return risk;

    return risk;
  }

  setDataFromQuotation(quotation: HealthModels.QuotationSummary) {
    this.patchData({
      package: quotation.package,
      brokerFees: quotation.brokerFeesChoice,
      currentStaggering: quotation.staggering,
      formulaChoice: quotation.formulaChoice,
      quotationCreationDate: quotation.creationDate,
      signatureReadyDate: dateFromISOString(
        quotation.signatureReadyDate ?? '',
      )!,
      quotationForm: getQuotationFormFromRisk(
        quotation.risk,
        quotation.formulaChoice.formula,
      ),
    });

    this.sessionStorage.setObject(
      SESSION_STORAGE_KEYS.HealthQuotationData,
      this.currentData,
    );
  }

  clearStoredData() {
    this.currentData = null;
    this.sessionStorage.clearItem(SESSION_STORAGE_KEYS.HealthQuotationData);
  }

  patchData(data: Partial<HealthModels.HealthQuotationStoredData>) {
    this.currentData = Object.assign({}, this.currentData, data);
    this.currentData.quotationNavigator = this.getInfoNavigator();
    this.sessionStorage.setObject(
      SESSION_STORAGE_KEYS.HealthQuotationData,
      this.currentData,
    );
  }

  setData(data: HealthModels.HealthQuotationStoredData) {
    // donner des valeurs par defaut pour l'ancien formulaire pour les cachers
    data.quotationForm.vadInfo.isVad = true;
    data.quotationForm.vadInfo.toggleBloctel = true;
    data.quotationForm.vadInfo.isBloctel = false;
    data.quotationForm.vadInfo.isCallFromProspect = true;
    data.quotationForm.vadInfo.saleType = 1;
    data.quotationNavigator = this.getInfoNavigator();

    this.currentData = Object.assign({}, data);
    this.sessionStorage.setObject(
      SESSION_STORAGE_KEYS.HealthQuotationData,
      this.currentData,
    );
  }

  protected internalClear() {
    this.currentData = null;
    this.keepCurrentRisk = false;
  }

  goToPricing(quotationForm: HealthModels.QuotationForm): void {
    this.patchData({ quotationForm });
    this.goToStep(1);
  }

  getTerminationUrl(
    assure: CommonRiskModels.AssureInfos | any,
    ria: Resiliation | undefined | null,
    policyId: any,
    type: InsuranceTypeEnum,
  ): string {
    let ad = new Address(assure);
    const httpParams = new HttpParams()
      .set('first_name', assure.prenom ?? '')
      .set('last_name', assure.nom ?? '')
      .set('kind_of_contract_id', type === InsuranceTypeEnum.Sante ? '6' : '4')
      .set(
        'address',
        ad && typeof ad.formattedAddress === 'function'
          ? ad.formattedAddress()
          : '',
      )
      .set('post_code', assure.address?.cityZipCode ?? '')
      .set('city', assure.address?.cityName ?? '')
      .set('email', assure.email ?? '')
      .set(
        'gender',
        assure.civilite == CivilityEnum.Femme ? 'Madame' : 'Monsieur',
      )
      .set('cancel_reason_id', type === InsuranceTypeEnum.Sante ? '22' : '13')
      .set('contract_reference', ria?.currentContractNumber ?? '')
      .set('new_insurer', ria?.currentContractCarrierName ?? '')
      .set('private_reference', policyId?.toString() ?? '')
      .set('data_source', 'WAZARI');

    if (!!ria && ria.currentContractCarrierId) {
      httpParams.set('recipient_id', ria.currentContractCarrierId.toString());
    }
    const queryParamsString = httpParams.toString();
    let baseURL = 'https://jeresiliemoncontrat.com/resilie/agent/autocreate';
    const url = `${baseURL}?${queryParamsString}`;

    return url;
  }

  remaining(signatureReady: any): string {
    let date = moment().utc().toISOString();
    return formatDistance(signatureReady, date, {
      locale: fr,
      includeSeconds: true,
    });
  }

  remaining2(signatureReady: any): number {
    let dateUtc = moment().utc().toDate();
    let dateRight = moment(signatureReady).toDate();
    let number = differenceInMinutes(dateRight, dateUtc);
    return number;
  }

  isMobileNavigator() {
    let check = false;
    let a =
      navigator.userAgent ||
      navigator.vendor ||
      (window as any).opera ||
      navigator.platform;
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      check = true;
    return check;
  }

  getInfoNavigator() {
    let nav = '';
    let navigatorInfo: any = navigator;
    let iPAddress = '';

    let userAgent = navigatorInfo.userAgent;

    let infos = userAgent.split(' ');
    let length = infos.length;

    let navInfo = infos[length - 1];
    if (
      navInfo &&
      !infos[length - 2].match(/(Gecko)/g) &&
      !infos[length - 2].match(/(Mobile)/g) &&
      !infos[length - 2].match(/(Safari)/g)
    ) {
      navInfo = infos[length - 2];
    }

    if (
      navInfo &&
      !infos[length - 3].match(/(Gecko)/g) &&
      infos[length - 2].match(/(Mobile)/g) &&
      infos[length - 1].match(/(Safari)/g) &&
      !userAgent.match(/iPhone|iPod|iPad|Mac OS/g)
    ) {
      navInfo = infos[length - 3];
    }
    let infoNav = navInfo.split('/');
    var regExp = /\(([^)]+)\)/;
    var matches = regExp.exec(userAgent);
    let platform: any = '';
    if (matches && matches[1]) {
      platform = matches[1];
      platform = platform.split(';');
      platform = platform[0];
    }
    nav =
      infoNav[0] +
      ' (v' +
      infoNav[1] +
      ')' +
      (platform ? ' sur ' + platform : '');

    if (navigatorInfo.userAgentData) {
      return {
        userAgent:
          navigator.userAgent || navigator.vendor || (window as any).opera,
        isDesktop: !(
          navigatorInfo.userAgentData.mobile || this.isMobileNavigator()
        ),
        iPAddress: iPAddress,
        navigator: nav,
      };
    } else {
      return {
        userAgent:
          navigator.userAgent || navigator.vendor || (window as any).opera,
        isDesktop: !this.isMobileNavigator(),
        iPAddress: iPAddress,
        navigator: nav,
      };
    }
  }
}
